import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import VideoBackground from '../../../components/base/VideoBackground';
import FloatingSocialIcons from '../../../components/base/FloatingSocialIcons';

const GoogleMyBusinessPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 relative overflow-hidden">
      <VideoBackground />
      <FloatingSocialIcons />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 via-teal-600 to-green-600 bg-clip-text text-transparent mb-6">
            Google My Business Management
          </h1>
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Maximize your Google presence and attract more local customers with professional Google My Business optimization.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
            Benefits for Small Businesses
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Local Visibility</h3>
              <p className="text-gray-600">Appear in local search results and Google Maps to attract nearby customers.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-green-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-star-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Build Trust</h3>
              <p className="text-gray-600">Showcase reviews, photos, and business information to build customer trust.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-6 rounded-2xl shadow-lg border border-white/50">
              <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-phone-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">Drive Actions</h3>
              <p className="text-gray-600">Get more calls, visits, and website clicks from potential customers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-teal-600 to-green-600 bg-clip-text text-transparent">
            Our GMB Services Include
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-blue-100 to-teal-100 p-6 rounded-xl">
              <i className="ri-settings-4-line text-3xl text-blue-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Profile Optimization</h4>
              <p className="text-sm text-gray-600">Complete setup and optimization of your GMB profile</p>
            </div>
            <div className="bg-gradient-to-br from-teal-100 to-green-100 p-6 rounded-xl">
              <i className="ri-camera-line text-3xl text-teal-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Photo Management</h4>
              <p className="text-sm text-gray-600">Regular posting of high-quality business photos</p>
            </div>
            <div className="bg-gradient-to-br from-green-100 to-yellow-100 p-6 rounded-xl">
              <i className="ri-message-3-line text-3xl text-green-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Review Management</h4>
              <p className="text-sm text-gray-600">Monitor and respond to customer reviews</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-100 to-orange-100 p-6 rounded-xl">
              <i className="ri-calendar-event-line text-3xl text-yellow-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Posts & Updates</h4>
              <p className="text-sm text-gray-600">Regular posts about offers, events, and news</p>
            </div>
            <div className="bg-gradient-to-br from-orange-100 to-red-100 p-6 rounded-xl">
              <i className="ri-bar-chart-2-line text-3xl text-orange-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Performance Tracking</h4>
              <p className="text-sm text-gray-600">Monthly insights and analytics reports</p>
            </div>
            <div className="bg-gradient-to-br from-red-100 to-pink-100 p-6 rounded-xl">
              <i className="ri-question-answer-line text-3xl text-red-600 mb-3"></i>
              <h4 className="font-bold text-gray-800 mb-2">Q&A Management</h4>
              <p className="text-sm text-gray-600">Manage customer questions and answers</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Links */}
      <section className="py-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-xl font-bold mb-6 text-gray-800">Follow Our Local Marketing Tips</h3>
          <div className="flex justify-center space-x-6">
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-facebook-fill text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-teal-500 to-green-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-instagram-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-green-500 to-yellow-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-twitter-line text-xl"></i>
            </a>
            <a href="#" className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center text-white hover:scale-110 transition-transform">
              <i className="ri-linkedin-fill text-xl"></i>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-blue-500 via-teal-500 to-green-500 rounded-3xl p-8 text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Dominate Local Search?</h2>
          <p className="text-xl mb-8">Let's optimize your Google My Business profile for maximum visibility!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/consultation"
              className="bg-white text-teal-600 px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors"
            >
              Book Free Consultation
            </Link>
            <Link 
              to="/quote"
              className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-teal-600 transition-colors"
            >
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GoogleMyBusinessPage;